import { Table, Space, Typography, Button } from 'antd';
import {
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useState, forwardRef, useImperativeHandle } from 'react';
import ReactModal from 'react-modal';
import {
  Header,
} from '../../../../../core/components';
import { JobApplicationStagesService } from '../services/job-application-stages-service';
import { JobApplicationStatusNote } from '../models/job-application-status.model';
import { UtilService } from '../../../../../core/services/util.service';
import { useProfile } from '../../profile/components/ProfileContext';
import { jo } from '../../../../../theme/css-vars';

const { Text } = Typography;

interface Props {
  data: JobApplicationStatusNote[];
  statusId: string;
  onAddNote?: () => void;
}

export interface StageNotesTableRef {
  openAddNoteModal: () => void;
}

const StageNotesTable = forwardRef<StageNotesTableRef, Props>(
  ({ data, statusId, onAddNote }, ref) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingNote, setEditingNote] =
      useState<JobApplicationStatusNote | null>(null);
    const [editNoteContent, setEditNoteContent] = useState('');
    const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
    const { profile } = useProfile();

    useImperativeHandle(ref, () => ({
      openAddNoteModal: () => {
        onAddNote?.();
      },
    }));

    const canEditNote = (note: JobApplicationStatusNote): boolean => {
      if (!profile || !note.createdBy) return false;

      const currentUserEmail = profile.email;
      const noteCreator = note.createdBy;

      if (noteCreator.includes('@')) {
        return currentUserEmail === noteCreator;
      }

      const currentUserUsername = currentUserEmail.split('@')[0];
      return currentUserUsername === noteCreator;
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };

    const transformStatusNotes = (notes: JobApplicationStatusNote[]) => {
      return notes.map((note, index) => {
        let parsedContent = note.content;
        try {
          const parsed = JSON.parse(note.content);
          if (typeof parsed === 'object' && parsed.content) {
            parsedContent = parsed.content;
          } else if (typeof parsed === 'string') {
            parsedContent = parsed;
          }
        } catch {
          parsedContent = note.content;
        }

        return {
          id: note.id || `note-${index}-${note.createdDate}`,
          content: parsedContent,
          createdBy: note.createdBy,
          createdDate: note.createdDate,
          title: note.title,
          type: note.type,
          category: note.category,
        };
      });
    };

    const sortedData = transformStatusNotes(data).sort(
      (a, b) =>
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );

    const truncateText = (
      text: string,
      maxWords: number = 20
    ): { truncated: string; full: string; needsTruncation: boolean } => {
      const words = text.trim().split(/\s+/);
      if (words.length <= maxWords) {
        return { truncated: text, full: text, needsTruncation: false };
      }

      const truncated = words.slice(0, maxWords).join(' ') + '...';
      return { truncated, full: text, needsTruncation: true };
    };

    const toggleNoteExpansion = (noteId: string) => {
      const newExpandedNotes = new Set(expandedNotes);
      if (newExpandedNotes.has(noteId)) {
        newExpandedNotes.delete(noteId);
      } else {
        newExpandedNotes.add(noteId);
      }
      setExpandedNotes(newExpandedNotes);
    };

    const renderDescription = (content: string, noteId: string) => {
      const { truncated, full, needsTruncation } = truncateText(content, 20);
      const isExpanded = expandedNotes.has(noteId);
      const isMobile = window.innerWidth < 768;

      return (
        <div style={{ maxWidth: isMobile ? '250px' : '400px' }}>
          <Text style={{ fontSize: '14px', lineHeight: '1.5' }}>
            {isExpanded ? full : truncated}
          </Text>
          {needsTruncation && (
            <Button
              type="link"
              size="small"
              style={{
                padding: '0',
                height: 'auto',
                fontSize: '12px',
                marginLeft: '8px',
              }}
              onClick={() => toggleNoteExpansion(noteId)}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </Button>
          )}
        </div>
      );
    };

    const handleDeleteNote = async (noteId: string) => {
      try {
        await JobApplicationStagesService.DeleteStageNote(statusId, noteId);

        UtilService.Alert(
          'Success!',
          'success',
          'Note has been deleted successfully.'
        );

        if (onAddNote) {
          onAddNote();
        }
      } catch (error) {
        console.error('Error deleting note:', error);
        UtilService.Alert(
          'Error!',
          'error',
          'Failed to delete note. Please try again.'
        );
      }
    };

    const handleEditNote = (note: JobApplicationStatusNote) => {
      setEditingNote(note);
      setEditNoteContent(note.content);
      setIsEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
      if (!editingNote || !editNoteContent.trim()) {
        return;
      }

      try {
        await JobApplicationStagesService.UpdateStageNote(
          statusId,
          editingNote.id,
          editNoteContent.trim()
        );

        setIsEditModalOpen(false);
        setEditingNote(null);
        setEditNoteContent('');
        
        UtilService.Alert(
          'Success!',
          'success',
          'Note has been updated successfully.'
        );

        if (onAddNote) {
          onAddNote();
        }
      } catch (error) {
        console.error('Error updating note:', error);
        UtilService.Alert(
          'Error!',
          'error',
          'Failed to update note. Please try again.'
        );
      }
    };

    const handleCloseEditModal = () => {
      setIsEditModalOpen(false);
      setEditingNote(null);
      setEditNoteContent('');
    };

    const columns = [
      {
        title: 'Created Date',
        dataIndex: 'createdDate',
        key: 'createdDate',
        align: 'center' as const,
        width: 150,
        render: (date: string) => (
          <Text style={{ fontSize: '14px' }}>{formatDate(date)}</Text>
        ),
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        align: 'center' as const,
        width: 120,
        render: (createdBy: string) => (
          <Text style={{ fontSize: '14px' }}>
            {createdBy.includes('@') ? createdBy.split('@')[0] : createdBy}
          </Text>
        ),
      },
      {
        title: 'Description',
        dataIndex: 'content',
        key: 'content',
        width: window.innerWidth < 768 ? 280 : undefined,
        render: (content: string, record: JobApplicationStatusNote) =>
          renderDescription(content, record.id),
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'center' as const,
        width: 120,
        render: (_: any, record: JobApplicationStatusNote) => {
          const canEdit = canEditNote(record);
          return (
            <Space size="small">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditNote(record)}
                title={
                  canEdit ? 'Edit Note' : 'You can only edit your own notes'
                }
                style={{ color: canEdit ? jo.warning : jo.borderMuted }}
                disabled={!canEdit}
              />
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteNote(record.id)}
                title={
                  canEdit ? 'Delete Note' : 'You can only delete your own notes'
                }
                style={{ color: canEdit ? jo.danger : jo.borderMuted }}
                disabled={!canEdit}
              />
            </Space>
          );
        },
      },
    ];

    return (
      <>
        <div style={{ padding: '20px 0' }}>
          <Table
            columns={columns}
            dataSource={sortedData}
            rowKey="id"
            pagination={false}
            size="small"
            style={{
              backgroundColor: jo.surface,
              borderRadius: '8px',
              overflow: 'hidden',
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>

        <ReactModal
          isOpen={isEditModalOpen}
          onRequestClose={() => {}}
          className="bg-white rounded-lg p-6 max-w-lg mx-auto shadow-lg w-full scrollbar-hide"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
          ariaHideApp={false}
        >
          <Header
            title="Edit Note"
            buttons={[
              {
                icon: {
                  value: <CloseCircleOutlined />,
                  size: 'text-lg',
                },
                text: {
                  size: 'md',
                  value: 'Close',
                  weight: 500,
                },
                color: 'red',
                tone: 500,
                onClick: handleCloseEditModal,
              },
            ]}
          />

          <div>
            <div className="grid grid-cols-1 mb-5 gap-x-5 gap-y-5">
              <div>
                <textarea
                  placeholder="Enter note content..."
                  value={editNoteContent}
                  onChange={(e) => setEditNoteContent(e.target.value)}
                  className="ac-note-textarea"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end">
            <button
              type="button"
              className="ac-btn ac-btn-primary"
              disabled={!editNoteContent.trim()}
              onClick={handleSaveEdit}
            >
              Update Note
            </button>
          </div>
        </ReactModal>
      </>
    );
  }
);

export default StageNotesTable;
